import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import { useAuth } from '@/features/auth/AuthContext';

type Entity = {
  id: number;
  name: string;
  type: string;
  legal_name: string | null;
  trade_name: string | null;
  has_document: boolean;
  document_type: string | null;
  email: string | null;
  phone: string | null;
  control_start_date: string | null;
  color: string | null;
  status: string;
  simplified_debt_mode: boolean;
  notes: string | null;
  usage?: { transactions: number; accounts: number; cards: number; loans: number; total: number };
};

const TYPES = [
  { value: 'pessoa_fisica', label: 'Pessoa física' },
  { value: 'empresa', label: 'Empresa' },
  { value: 'familia', label: 'Família' },
  { value: 'casal', label: 'Casal' },
  { value: 'grupo_economico', label: 'Grupo econômico' },
  { value: 'projeto', label: 'Projeto' },
  { value: 'centro_de_custo', label: 'Centro de custo' },
];

const COLORS = ['#2563eb', '#059669', '#7c3aed', '#db2777', '#ea580c', '#0891b2', '#64748b'];

function typeLabel(value: string): string {
  return TYPES.find((t) => t.value === value)?.label ?? value;
}

/**
 * Entidades — RF-ENT-01 a RF-ENT-03.
 *
 * Ate aqui as entidades so existiam pela semeadura: quem abrisse uma segunda
 * empresa nao tinha como registra-la.
 */
export function EntitiesPage({ selectedIds }: { selectedIds: number[] }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Entity | null>(null);
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();

  const entities = useQuery({
    queryKey: ['entities', selectedIds],
    queryFn: () => api.get<{ data: Entity[] }>('/entities', { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/entities/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => { window.alert(res.message); refresh(); },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao excluir.'),
  });

  function refresh() {
    setCreating(false);
    setEditing(null);
    void queryClient.invalidateQueries({ queryKey: ['entities'] });
    void queryClient.invalidateQueries({ queryKey: ['reference'] });

    /**
     * O seletor de contexto da barra superior le `user.entities`, que vem de
     * `/auth/me` e e carregado UMA vez, no login. Sem recarregar aqui, a
     * entidade recem-criada nao aparece no seletor — o usuario ve a mensagem
     * de sucesso e conclui que nao funcionou.
     *
     * Invalidar uma query nao resolve: o usuario nao vive no cache do
     * react-query, vive no AuthContext.
     */
    void refreshUser();
  }

  const list = entities.data ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Entidades</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Empresas, pessoas e grupos que este sistema acompanha.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          + Nova entidade
        </button>
      </div>

      {entities.isPending ? (
        <div className="mt-5 grid animate-pulse gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-slate-200 dark:bg-slate-800" />
          ))}
        </div>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {list.map((e) => (
            <div
              key={e.id}
              className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {e.name}
                    {e.status !== 'ativa' && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                        {e.status}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {typeLabel(e.type)}
                    {e.legal_name && ` · ${e.legal_name}`}
                  </p>
                </div>
                <span
                  className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: e.color ?? '#94a3b8' }}
                />
              </div>

              <dl className="mt-3 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                <div className="flex justify-between">
                  <dt>Controle desde</dt>
                  <dd>{formatDate(e.control_start_date)}</dd>
                </div>
                {e.has_document && (
                  <div className="flex justify-between">
                    <dt>{e.document_type?.toUpperCase()}</dt>
                    {/* O documento fica criptografado e não volta na listagem. */}
                    <dd className="text-slate-400">cadastrado</dd>
                  </div>
                )}
                {e.usage && (
                  <div className="flex justify-between">
                    <dt>Registros</dt>
                    <dd>
                      {e.usage.accounts} conta(s) · {e.usage.transactions} lançamento(s)
                    </dd>
                  </div>
                )}
              </dl>

              <div className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-2.5 text-[11px] dark:border-slate-800">
                <button
                  onClick={() => setEditing(e)}
                  className="text-slate-500 hover:underline dark:text-slate-400"
                >
                  editar
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(
                      `Excluir a entidade "${e.name}"?\n\n` +
                      'Se houver contas, lançamentos ou dívidas, ela será encerrada em vez de ' +
                      'excluída — todo o histórico depende dela continuar existindo.',
                    )) {
                      remove.mutate(e.id);
                    }
                  }}
                  className="ml-auto text-rose-600 hover:underline dark:text-rose-400"
                >
                  excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-400">
        A data de início do controle define o que o sistema acompanha: lançamento anterior a ela é
        bloqueado, e o saldo inicial de cada conta não pode ser mais antigo.
      </p>

      {(creating || editing) && (
        <EntityForm
          entity={editing}
          selectedIds={selectedIds}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function EntityForm({
  entity, selectedIds, onClose, onSaved,
}: {
  entity: Entity | null;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = entity !== null;
  const today = new Date().toISOString().slice(0, 10);

  const [name, setName] = useState(entity?.name ?? '');
  const [type, setType] = useState(entity?.type ?? 'pessoa_fisica');
  const [legalName, setLegalName] = useState(entity?.legal_name ?? '');
  const [tradeName, setTradeName] = useState(entity?.trade_name ?? '');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState(entity?.email ?? '');
  const [phone, setPhone] = useState(entity?.phone ?? '');
  const [controlStart, setControlStart] = useState(
    entity?.control_start_date ?? `${new Date().getFullYear()}-01-01`,
  );
  const [color, setColor] = useState(entity?.color ?? COLORS[0]!);
  const [status, setStatus] = useState(entity?.status ?? 'ativa');

  const isCompany = type === 'empresa';

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        ...(isEdit ? {} : { type }),
        ...(legalName ? { legal_name: legalName } : {}),
        ...(tradeName ? { trade_name: tradeName } : {}),
        ...(document ? { document } : {}),
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        control_start_date: controlStart,
        color,
        ...(isEdit ? { status } : {}),
      };

      return isEdit
        ? api.patch(`/entities/${entity.id}`, payload, { entityIds: selectedIds })
        : api.post('/entities', payload, { entityIds: selectedIds });
    },
    onSuccess: (res) => {
      const meta = (res as { meta?: { note?: string; warning?: string | null } })?.meta;
      if (meta?.warning) window.alert(meta.warning);
      else if (meta?.note) window.alert(meta.note);
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  const canSubmit =
    name.trim().length >= 2 &&
    controlStart !== '' &&
    controlStart <= today &&
    (!isCompany || isEdit || legalName.trim() !== '');

  return (
    <Modal title={isEdit ? 'Editar entidade' : 'Nova entidade'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.values(error.errors).map((m, i) => <li key={i}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Nome" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            placeholder="Como você chama esta entidade no dia a dia"
            className="input w-full"
          />
        </Field>

        <Field label="Tipo" required hint={isEdit ? 'não pode ser alterado' : undefined}>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={isEdit}
            className="input w-full disabled:opacity-60"
          >
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>

        {isCompany && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Razão social" required={!isEdit}>
              <input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                className="input w-full"
              />
            </Field>
            <Field label="Nome fantasia">
              <input
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
                className="input w-full"
              />
            </Field>
          </div>
        )}

        <Field
          label={isCompany ? 'CNPJ' : 'CPF'}
          hint={
            entity?.has_document
              ? 'já cadastrado — preencha só para substituir'
              : 'guardado criptografado'
          }
        >
          <input
            value={document}
            onChange={(e) => setDocument(e.target.value)}
            inputMode="numeric"
            placeholder={isCompany ? '00.000.000/0000-00' : '000.000.000-00'}
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="E-mail">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input w-full"
            />
          </Field>
          <Field label="Telefone">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input w-full" />
          </Field>
        </div>

        {/* RF-ENT-08 — o campo mais consequente do formulário. */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <Field label="Controle começa em" required>
            <input
              type="date"
              value={controlStart}
              onChange={(e) => setControlStart(e.target.value)}
              required
              max={today}
              className="input w-full"
            />
          </Field>
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            Lançamento anterior a esta data é bloqueado, e o saldo inicial das contas desta entidade
            não pode ser mais antigo. Você não precisa importar anos de histórico — informe a data em
            que decidiu começar a controlar.
          </p>
          {isEdit && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Adiantar esta data é recusado se já houver lançamentos antes dela.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Cor
            </span>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{ background: c }}
                  className={`h-7 w-7 rounded-lg transition ${
                    color === c
                      ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-white dark:ring-offset-slate-900'
                      : ''
                  }`}
                  aria-label={`Cor ${c}`}
                />
              ))}
            </div>
          </div>
          {isEdit && (
            <Field label="Situação">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="input w-full"
              >
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
                <option value="encerrada">Encerrada</option>
              </select>
            </Field>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
